import { supabase } from './supabase';
import { getIntakeTemplateWithFields, type IntakeTemplateWithSections } from './crmIntakeFormTemplates';
import {
  buildPublicIntakeUrl,
  getIntakePublicOriginFromBusinessRow,
  type IntakeCustomHostFields,
} from './intakePublicUrl';
import { sendSMS } from './messaging';
import { sendEmailWithFallback } from './email';
import { recordOpportunityActivity } from './opportunityActivities';

export type IntakeInstanceChannel = 'sms' | 'email';
export type IntakeInstanceStatus = 'sent' | 'opened' | 'in_progress' | 'submitted';

export interface IntakeFieldSnapshot {
  key: string;
  field_type: string;
  label: string;
  is_required: boolean;
  options: string[];
  /** Grouping for sectioned forms (new sends); absent on older instances. */
  section_id?: string;
  section_title?: string;
  section_order?: number;
  field_order?: number;
}

export interface IntakeFormInstanceRow {
  id: string;
  business_id: string;
  opportunity_id: number | null;
  template_id: string | null;
  title_snapshot: string;
  fields_snapshot: IntakeFieldSnapshot[];
  access_token: string;
  customer_id: string;
  channel: IntakeInstanceChannel;
  status: IntakeInstanceStatus;
  responses: Record<string, string | boolean | number>;
  first_opened_at: string | null;
  submitted_at: string | null;
  created_at: string;
  updated_at: string;
  is_template_preview?: boolean;
  preview_expires_at?: string | null;
}

/** Build immutable snapshot for one send (new UUID per field + per section group). */
export function buildFieldsSnapshotFromTemplate(data: IntakeTemplateWithSections): IntakeFieldSnapshot[] {
  const out: IntakeFieldSnapshot[] = [];
  let sectionOrder = 0;
  for (const { section, fields } of data.sections) {
    const sectionSnapId = crypto.randomUUID();
    const sectionTitle = (section.title || '').trim() || `Section ${sectionOrder + 1}`;
    let fieldOrder = 0;
    for (const f of fields) {
      out.push({
        key: crypto.randomUUID(),
        field_type: f.field_type,
        label: f.label,
        is_required: f.is_required,
        options: Array.isArray(f.options) ? f.options.map(String) : [],
        section_id: sectionSnapId,
        section_title: sectionTitle,
        section_order: sectionOrder,
        field_order: fieldOrder++,
      });
    }
    sectionOrder++;
  }
  return out;
}

/** Resolves https origin for intake links (verified custom host or main app). */
export async function fetchIntakePublicOriginForBusinessId(businessId: string): Promise<string> {
  if (!supabase) throw new Error('Supabase client is not initialized');
  const { data, error } = await supabase
    .from('businesses')
    .select('intake_custom_hostname, intake_custom_host_verified_at')
    .eq('id', businessId)
    .maybeSingle();
  if (error) throw error;
  return getIntakePublicOriginFromBusinessRow(data as IntakeCustomHostFields | null);
}

/** When `businessRow` is omitted or unverified, uses the default app site URL. */
export function getPublicIntakeUrl(accessToken: string, businessRow?: IntakeCustomHostFields | null): string {
  const origin = getIntakePublicOriginFromBusinessRow(businessRow ?? null);
  return buildPublicIntakeUrl(accessToken, origin);
}

export const INTAKE_PHOTO_BUCKET = 'crm-intake-uploads';

export function intakePhotoMaxFromOptions(options: string[] | undefined): number {
  const n = parseInt(String(options?.[0] ?? '5'), 10);
  if (!Number.isFinite(n) || n < 1) return 5;
  return Math.min(20, Math.max(1, n));
}

export function normalizeIntakePhotoUrls(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((x) => String(x).trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    const t = value.trim();
    if (t.startsWith('[')) {
      try {
        const p = JSON.parse(t) as unknown;
        if (Array.isArray(p)) return p.map((x) => String(x).trim()).filter(Boolean);
      } catch {
        /* fall through */
      }
    }
    if (t) return [t];
  }
  return [];
}

/** Whether the saved response counts as answered for progress (opportunity row, etc.). */
export function isIntakeFieldFilled(field: IntakeFieldSnapshot, responses: Record<string, unknown>): boolean {
  const v = responses[field.key];
  if (v === undefined) return false;
  switch (field.field_type) {
    case 'checkbox':
      return v === true || v === false || v === 'true' || v === 'false';
    case 'photo':
      return normalizeIntakePhotoUrls(v).length > 0;
    case 'number':
      if (v === null || v === '') return false;
      if (typeof v === 'number' && Number.isFinite(v)) return true;
      if (typeof v === 'string') {
        const t = v.trim();
        if (t === '') return false;
        return Number.isFinite(parseFloat(t));
      }
      return false;
    default:
      if (v === null) return false;
      if (typeof v === 'boolean') return true;
      if (typeof v === 'number') return Number.isFinite(v);
      if (typeof v === 'string') return v.trim() !== '';
      return false;
  }
}

export function getIntakeFormFillProgress(
  fields: IntakeFieldSnapshot[] | unknown,
  responses: Record<string, unknown>,
): { filled: number; total: number } {
  const list = Array.isArray(fields) ? (fields as IntakeFieldSnapshot[]) : [];
  const total = list.length;
  let filled = 0;
  for (const f of list) {
    if (f?.key && isIntakeFieldFilled(f, responses)) filled += 1;
  }
  return { filled, total };
}

function intakePhotoStoragePathFromPublicUrl(publicUrl: string): string | null {
  const marker = `/object/public/${INTAKE_PHOTO_BUCKET}/`;
  const lower = publicUrl.toLowerCase();
  const idx = lower.indexOf(marker.toLowerCase());
  if (idx === -1) return null;
  return decodeURIComponent(publicUrl.slice(idx + marker.length).split('?')[0] || '');
}

export async function uploadIntakeFormPhoto(args: {
  token: string;
  fieldKey: string;
  file: File;
}): Promise<string> {
  if (!supabase) throw new Error('Supabase client is not initialized');
  const blob = args.file;
  const ft = (blob.type || '').toLowerCase();
  const ext =
    ft === 'image/png'
      ? 'png'
      : ft === 'image/webp'
        ? 'webp'
        : ft === 'image/gif'
          ? 'gif'
          : ft.includes('heic') || ft.includes('heif')
            ? 'heic'
            : 'jpg';
  const path = `${args.token}/${args.fieldKey}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from(INTAKE_PHOTO_BUCKET).upload(path, blob, {
    cacheControl: '3600',
    upsert: false,
    contentType: blob.type || undefined,
  });
  if (error) throw error;
  const { data } = supabase.storage.from(INTAKE_PHOTO_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

export async function removeIntakeFormPhoto(publicUrl: string): Promise<void> {
  if (!supabase) throw new Error('Supabase client is not initialized');
  const path = intakePhotoStoragePathFromPublicUrl(publicUrl);
  if (!path) throw new Error('Invalid photo URL');
  const { error } = await supabase.storage.from(INTAKE_PHOTO_BUCKET).remove([path]);
  if (error) throw error;
}

export type PublicIntakeLoadResult =
  | {
      ok: true;
      title: string;
      fields: IntakeFieldSnapshot[];
      responses: Record<string, unknown>;
      status: string;
      submitted_at: string | null;
      business_name: string;
      business_logo_url: string;
    }
  | { ok: false; error: string };

export async function publicIntakeGetByToken(token: string): Promise<PublicIntakeLoadResult> {
  if (!supabase) throw new Error('Supabase client is not initialized');
  const { data, error } = await supabase.rpc('public_intake_get_by_token', { p_token: token });
  if (error) throw error;
  const row = data as Record<string, unknown>;
  if (!row?.ok) {
    return { ok: false, error: String(row?.error || 'not_found') };
  }
  return {
    ok: true,
    title: String(row.title || ''),
    fields: Array.isArray(row.fields) ? (row.fields as IntakeFieldSnapshot[]) : [],
    responses: (row.responses && typeof row.responses === 'object' ? row.responses : {}) as Record<string, unknown>,
    status: String(row.status || 'sent'),
    submitted_at: row.submitted_at ? String(row.submitted_at) : null,
    business_name: String(row.business_name || ''),
    business_logo_url: String(row.business_logo_url || ''),
  };
}

export async function publicIntakeSaveDraft(
  token: string,
  responses: Record<string, unknown>,
  submitted: boolean
): Promise<{ ok: boolean; error?: string; status?: string }> {
  if (!supabase) throw new Error('Supabase client is not initialized');
  const { data, error } = await supabase.rpc('public_intake_save_draft', {
    p_token: token,
    p_responses: responses,
    p_submitted: submitted,
  });
  if (error) throw error;
  const row = data as Record<string, unknown>;
  if (!row?.ok) {
    return { ok: false, error: String(row?.error || 'unknown') };
  }
  return { ok: true, status: row.status != null ? String(row.status) : undefined };
}

export async function listIntakeInstancesForOpportunity(opportunityId: number): Promise<IntakeFormInstanceRow[]> {
  if (!supabase) throw new Error('Supabase client is not initialized');
  const { data, error } = await supabase
    .from('crm_intake_form_instances')
    .select('*')
    .eq('opportunity_id', opportunityId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []).map(normalizeInstanceRow);
}

function normalizeInstanceRow(row: any): IntakeFormInstanceRow {
  return {
    ...row,
    fields_snapshot: Array.isArray(row.fields_snapshot) ? row.fields_snapshot : [],
    responses: row.responses && typeof row.responses === 'object' ? row.responses : {},
    opportunity_id: row.opportunity_id == null ? null : Number(row.opportunity_id),
  };
}

const PREVIEW_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Creates a real intake instance so `/intake/:token` matches the customer experience; expires after 7 days. */
export async function createTemplatePreviewInstance(params: {
  businessId: string;
  templateId: string;
}): Promise<{ accessToken: string }> {
  if (!supabase) throw new Error('Supabase client is not initialized');

  const loaded = await getIntakeTemplateWithFields(params.templateId);
  if (!loaded) throw new Error('Template not found');
  if (loaded.template.business_id !== params.businessId) {
    throw new Error('Template does not belong to this business');
  }

  const fields_snapshot = buildFieldsSnapshotFromTemplate(loaded);
  const title_snapshot = loaded.template.title;
  const preview_expires_at = new Date(Date.now() + PREVIEW_TTL_MS).toISOString();

  const { data: inserted, error: insErr } = await supabase
    .from('crm_intake_form_instances')
    .insert({
      business_id: params.businessId,
      opportunity_id: null,
      customer_id: null,
      template_id: params.templateId,
      title_snapshot,
      fields_snapshot,
      channel: 'email',
      status: 'sent',
      responses: {},
      is_template_preview: true,
      preview_expires_at,
    })
    .select('access_token')
    .single();

  if (insErr) throw insErr;
  if (!inserted?.access_token) throw new Error('Failed to create preview link');

  return { accessToken: inserted.access_token };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Plain-text email body → simple HTML; linkifies exact intake URL when present. */
function plainIntakeEmailBodyToHtml(plain: string, intakeUrl: string): string {
  const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const escUrl = esc(intakeUrl);
  let frag = esc(plain).replace(/\r\n/g, '\n').replace(/\n/g, '<br>');
  if (escUrl && frag.includes(escUrl)) {
    frag = frag.split(escUrl).join(`<a href="${encodeURI(intakeUrl)}">${escUrl}</a>`);
  }
  return `<div style="font-family:system-ui,sans-serif;font-size:15px;line-height:1.5">${frag}</div>`;
}

export type DeliverIntakeFormLinkParams = {
  businessId: string;
  opportunityId: number;
  customerId: string;
  channel: IntakeInstanceChannel;
  url: string;
  titleSnapshot: string;
  instanceId: string;
  customerPhone?: string | null;
  customerEmail?: string | null;
  customerFirstName?: string | null;
  resent?: boolean;
  /** When set, sent as-is (URL appended if missing). */
  smsBody?: string | null;
  emailSubject?: string | null;
  /** Plain text; converted to HTML. URL appended on its own line if missing. */
  emailBodyPlain?: string | null;
};

/** Sends the public intake URL via SMS or email (new send or resend). Optional message overrides for CRM review step. */
export async function deliverIntakeFormLink(params: DeliverIntakeFormLinkParams): Promise<void> {
  if (!supabase) throw new Error('Supabase client is not initialized');
  const firstName = (params.customerFirstName || 'there').trim() || 'there';

  let businessName = 'Our team';
  const { data: biz } = await supabase.from('businesses').select('name').eq('id', params.businessId).single();
  if (biz?.name) businessName = biz.name;

  const activityBase = {
    kind: 'crm_intake_form' as const,
    intake_instance_id: params.instanceId,
    title: params.titleSnapshot,
    url: params.url,
    ...(params.resent ? { resent: true as const } : {}),
  };

  if (params.channel === 'sms') {
    const phone = (params.customerPhone || '').trim();
    if (!phone) throw new Error('Customer has no phone number');
    let msg =
      params.smsBody != null && String(params.smsBody).trim()
        ? String(params.smsBody).trim()
        : `Hi ${firstName}, please complete this short form from ${businessName}:\n\n${params.url}\n\nThank you!`;
    if (!msg.includes(params.url)) {
      msg = `${msg.trimEnd()}\n\n${params.url}`;
    }
    const sms = await sendSMS({ businessId: params.businessId, to: phone, message: msg });
    if (!sms.success) throw new Error(sms.error || 'Failed to send SMS');
    await recordOpportunityActivity({
      businessId: params.businessId,
      customerId: params.customerId,
      activityType: 'sms_sent',
      activityData: activityBase,
      specificOpportunityId: params.opportunityId,
    });
  } else {
    const email = (params.customerEmail || '').trim();
    if (!email) throw new Error('Customer has no email address');
    const subject =
      params.emailSubject != null && String(params.emailSubject).trim()
        ? String(params.emailSubject).trim()
        : `${businessName} — please complete your form`;
    let bodyPlain =
      params.emailBodyPlain != null && String(params.emailBodyPlain).trim()
        ? String(params.emailBodyPlain).trim()
        : `Hi ${firstName},\n\nPlease complete this short form:\n\n${params.url}\n\nThank you,\n${businessName}`;
    if (!bodyPlain.includes(params.url)) {
      bodyPlain = `${bodyPlain.trimEnd()}\n\n${params.url}`;
    }
    const htmlBody = plainIntakeEmailBodyToHtml(bodyPlain, params.url);
    const mail = await sendEmailWithFallback({
      to: email,
      subject,
      body: htmlBody,
      businessId: params.businessId,
      customerId: params.customerId,
    });
    if (!mail.success) throw new Error(mail.error || 'Failed to send email');
    await recordOpportunityActivity({
      businessId: params.businessId,
      customerId: params.customerId,
      activityType: 'email_sent',
      activityData: activityBase,
      specificOpportunityId: params.opportunityId,
    });
  }
}

/** Creates intake row only (no SMS/email). Use with review modal, then `deliverIntakeFormLink`, or `deleteIntakeInstanceForBusiness` on cancel. */
export async function createIntakeInstanceOnly(params: {
  businessId: string;
  opportunityId: number;
  customerId: string;
  templateId: string;
  channel: IntakeInstanceChannel;
}): Promise<{ instanceId: string; accessToken: string; url: string; titleSnapshot: string }> {
  if (!supabase) throw new Error('Supabase client is not initialized');

  const loaded = await getIntakeTemplateWithFields(params.templateId);
  if (!loaded) throw new Error('Template not found');

  const fields_snapshot = buildFieldsSnapshotFromTemplate(loaded);
  const title_snapshot = loaded.template.title;

  const { data: inserted, error: insErr } = await supabase
    .from('crm_intake_form_instances')
    .insert({
      business_id: params.businessId,
      opportunity_id: params.opportunityId,
      template_id: params.templateId,
      title_snapshot,
      fields_snapshot,
      customer_id: params.customerId,
      channel: params.channel,
      status: 'sent',
      responses: {},
      is_template_preview: false,
      preview_expires_at: null,
    })
    .select('id, access_token')
    .single();

  if (insErr) throw insErr;
  if (!inserted?.access_token) throw new Error('Failed to create intake instance');

  const origin = await fetchIntakePublicOriginForBusinessId(params.businessId);
  const url = buildPublicIntakeUrl(inserted.access_token, origin);
  return {
    instanceId: inserted.id,
    accessToken: inserted.access_token,
    url,
    titleSnapshot: title_snapshot,
  };
}

export async function deleteIntakeInstanceForBusiness(businessId: string, instanceId: string): Promise<void> {
  if (!supabase) throw new Error('Supabase client is not initialized');
  const { error } = await supabase
    .from('crm_intake_form_instances')
    .delete()
    .eq('id', instanceId)
    .eq('business_id', businessId);
  if (error) throw error;
}

export async function createAndSendIntakeInstance(params: {
  businessId: string;
  opportunityId: number;
  customerId: string;
  templateId: string;
  channel: IntakeInstanceChannel;
  customerPhone?: string | null;
  customerEmail?: string | null;
  customerFirstName?: string | null;
}): Promise<{ instanceId: string; accessToken: string }> {
  const { instanceId, accessToken, url, titleSnapshot } = await createIntakeInstanceOnly({
    businessId: params.businessId,
    opportunityId: params.opportunityId,
    customerId: params.customerId,
    templateId: params.templateId,
    channel: params.channel,
  });

  await deliverIntakeFormLink({
    businessId: params.businessId,
    opportunityId: params.opportunityId,
    customerId: params.customerId,
    channel: params.channel,
    url,
    titleSnapshot,
    instanceId,
    customerPhone: params.customerPhone,
    customerEmail: params.customerEmail,
    customerFirstName: params.customerFirstName,
    resent: false,
  });

  return { instanceId, accessToken };
}

/** Resends the same public link for an existing intake instance (caller picks SMS or email). */
export async function resendIntakeInstanceLink(params: {
  businessId: string;
  opportunityId: number;
  customerId: string;
  channel: IntakeInstanceChannel;
  accessToken: string;
  instanceId: string;
  titleSnapshot: string;
  smsBody?: string | null;
  emailSubject?: string | null;
  emailBodyPlain?: string | null;
}): Promise<void> {
  if (!supabase) throw new Error('Supabase client is not initialized');

  const { data: cust, error: cErr } = await supabase
    .from('customers')
    .select('phone, email, first_name')
    .eq('id', params.customerId)
    .eq('business_id', params.businessId)
    .maybeSingle();
  if (cErr) throw cErr;

  const origin = await fetchIntakePublicOriginForBusinessId(params.businessId);
  const url = buildPublicIntakeUrl(String(params.accessToken), origin);
  await deliverIntakeFormLink({
    businessId: params.businessId,
    opportunityId: params.opportunityId,
    customerId: params.customerId,
    channel: params.channel,
    url,
    titleSnapshot: params.titleSnapshot,
    instanceId: params.instanceId,
    customerPhone: cust?.phone,
    customerEmail: cust?.email,
    customerFirstName: cust?.first_name,
    resent: true,
    smsBody: params.smsBody,
    emailSubject: params.emailSubject,
    emailBodyPlain: params.emailBodyPlain,
  });

  const { error: uErr } = await supabase
    .from('crm_intake_form_instances')
    .update({ channel: params.channel, updated_at: new Date().toISOString() })
    .eq('id', params.instanceId);
  if (uErr) throw uErr;
}
